/**
 * Apples Wetterschlüssel — als Symbol hier, als Wort in der Textdatei.
 *
 * **Zwei Listen mit denselben Schlüsseln, und das ist Absicht.** Die Kachel im
 * Kartenstapel zeigt ein Zeichen, das Blatt dahinter ein Wort; wer einen
 * Schlüssel ergänzt, ergänzt ihn an beiden Stellen. Fehlt eine, fällt der
 * Schlüssel auf sich selbst zurück statt auf eine falsche Sonne.
 *
 * Aufgeführt sind nur die häufigen. Was hier nicht steht, wird als Wort
 * **durchgereicht** und bekommt **kein** Symbol: Apple ergänzt die Liste, ohne
 * zu fragen, und ein erfundenes Zeichen wäre schlimmer als keines — ein
 * unbekannter Schlüssel ist wenigstens als solcher zu erkennen.
 *
 * **Die Wörter stehen seit `0.15.0` in `texte/de.ts`** (`karte.wetterlagen`),
 * die Zuordnung zum Zeichen hier. Derselbe Schnitt wie bei den Nadelfarben:
 * die Mechanik bleibt, wo sie wirkt.
 */
import { WORTLAUT } from '../texte'

/** Die acht Zeichen, die es gibt. `null` heißt: keines, nur die Temperatur. */
export type Lage =
  | 'klar'
  | 'teils'
  | 'wolkig'
  | 'regen'
  | 'schnee'
  | 'gewitter'
  | 'nebel'
  | 'wind'

const SCHLUESSEL: Record<string, Lage> = {
  Clear: 'klar',
  MostlyClear: 'klar',
  PartlyCloudy: 'teils',
  MostlyCloudy: 'wolkig',
  Cloudy: 'wolkig',
  Drizzle: 'regen',
  Rain: 'regen',
  HeavyRain: 'regen',
  Snow: 'schnee',
  Sleet: 'schnee',
  Hail: 'schnee',
  Thunderstorms: 'gewitter',
  Foggy: 'nebel',
  Haze: 'nebel',
  Windy: 'wind',
  Breezy: 'wind',
}

/** Der Schlüssel auf Deutsch — oder er selbst, wenn wir ihn nicht kennen. */
export function beschreibe(code: string): string {
  const worte: Record<string, string> = WORTLAUT.karte.wetterlagen
  return worte[code] ?? code
}

/** Welches Zeichen dazugehört — `null`, wenn wir den Schlüssel nicht kennen. */
export function lageVon(code: string): Lage | null {
  return SCHLUESSEL[code] ?? null
}

/** "aus SW": the eight points of the compass; the letters live in the texts. */
export function himmelsrichtung(grad: number): string {
  const striche: readonly string[] = WORTLAUT.karte.himmelsrichtungen
  return striche[Math.round((((grad % 360) + 360) % 360) / 22.5) % 16] ?? ''
}

/**
 * The colour of a temperature on the bar of the days ahead (0.33.0) -- from
 * cold blue to hot red, like the widget on the iPhone. A colour that encodes
 * a value is data, not appearance; it goes into the style attribute, as
 * `CLAUDE.md` allows for a colour from the data.
 */
export function temperaturFarbe(grad: number): string {
  if (grad <= 0) return '#7fc6ff'
  if (grad <= 10) return '#8fd3c7'
  if (grad <= 18) return '#d9d64a'
  if (grad <= 24) return '#f5b400'
  if (grad <= 29) return '#f28c28'
  return '#e5484d'
}
